import { shortDayByIndex } from "@/lib/helpers/date";
import type { Maybe } from "graphql/jsutils/Maybe";
import type { Job, Package } from "@/__generated__/graphql";
import { parseISO, format } from "date-fns";
import { CreditAccountType } from "@/types/profile";
import MainCreditAccount from "@/assets/images/3d-icons/main-credit-account.png";
import MassageCreditAccount from "@/assets/images/3d-icons/home-massage.png";
import OverviewCreditAccount from "@/assets/images/3d-icons/wallet.png";
import BeautyCreditAccount from "@/assets/images/3d-icons/home-beauty.png";
import PetCreditAccount from "@/assets/images/3d-icons/pet-sitting.png";

function getMonthName(dateString: string) {
  const date = parseISO(dateString);

  return `${format(date, "MMMM")}`;
}

export const getPackageJobMonthsName = (
  packageData: Package | null,
): string => {
  if (!packageData?.jobs) {
    return "-";
  }
  const months = packageData?.jobs
    ?.map((job) => job.startDate)
    .sort()
    .map((startDate) => getMonthName(startDate));

  const formattedMonths =
    months.length > 1
      ? `${months.slice(0, -1).join(", ")} and ${months[months.length - 1]}`
      : months[0];

  return formattedMonths;
};

export const mapJobsToSchedules = (jobs: Maybe<Job[]>): string[] => {
  if (!jobs) {
    return [];
  }
  const allTasks = jobs
    .flatMap((job) =>
      job.tasks.map(({ day, startTime, endTime }) => {
        const [startHour, startMinute] = startTime.split(":");
        const [endHour, endMinute] = endTime.split(":");

        return {
          day,
          start: `${startHour}.${startMinute}`,
          end: `${endHour}.${endMinute}`,
        };
      }),
    )
    .sort((a, b) => a.day - b.day);

  return allTasks.map(
    (task) => `${shortDayByIndex(task.day)} at ${task.start} - ${task.end}`,
  );
};

export const getExpirationsRequestInput = (expDate: string) => {
  const [expMonth, expYear] = expDate.split("/");

  return {
    expMonth,
    expYear,
  };
};

export const getCreditAccountBannerIcon = (type?: CreditAccountType) => {
  switch (type) {
    case "overview":
      return OverviewCreditAccount;
    case "general":
      return MainCreditAccount;
    case "massage":
      return MassageCreditAccount;
    case "beauty":
      return BeautyCreditAccount;
    case "pet":
      return PetCreditAccount;
    default:
      return OverviewCreditAccount;
  }
};

type AddressPart = string | null | undefined;

export const joinAddressParts = (...parts: AddressPart[]): string => {
  // Join non-empty address parts using the shared separator.

  return parts
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join("・");
};
